import { api } from './client';

/**
 * ============================================================================
 * THE CONSUMPTION-STATISTICS CLIENT — §14 / Appendix L §2.21, F13 / F15
 * ============================================================================
 *
 *   GET /pa/statistics/{id}?contract_id=[&preset=][&from=&to=]
 *
 * It mirrors `backend/internal/personalarea/stats_handlers.go`, pinned BOTH WAYS to
 * `api/openapi.yaml` by `src/api/wire-spec.test.ts`.
 *
 * ============================================================================
 * ⚠ THE TWO FACTS A SCREEN HERE CAN GET CATASTROPHICALLY WRONG
 * ============================================================================
 *
 * 1. **`data_available: false` DOES NOT MEAN «NO TRAFFIC».** A detailed-store client with no
 *    configured DSN returns an EMPTY SERIES WITH A NIL ERROR from every read, so a page
 *    written the obvious way renders «0 Б, 0 сессий» on an installation that cannot see its
 *    own traffic store — and a subscriber cannot tell that from the truth. The three server
 *    behaviours are separated on purpose and this client relays all three:
 *
 *      NOT CONFIGURED       → 200, `data_available: false`, an empty series.
 *      CONFIGURED + FAILING → **500**, deliberately loud: there is no honest number to print.
 *      CONFIGURED + EMPTY   → 200, `data_available: true`. The ONLY case allowed to say
 *                             «вы ничего не скачали».
 *
 *    ⚠ Even `true` means «the store answered», never «the store is complete»: the write path
 *    DROPS rows rather than stalling RADIUS, so a prolonged ClickHouse outage leaves that
 *    window permanently short.
 *
 * 2. **A DAY WITH NO TRAFFIC IS ABSENT, NOT ZERO.** `days` carries one row per day WITH
 *    DATA. A screen must not fabricate the missing days, and must not read a gap as a zero:
 *    the two are different facts and only the server knows which it sent.
 *
 * ============================================================================
 * ⚠ ONE TAB, AND THE COUNT IS A MEASUREMENT OF THE CLICKHOUSE SCHEMA
 * ============================================================================
 *
 * §14 draws four tabs. Verified against `backend/migrations/clickhouse/`: there is no hourly
 * summary at all («По часам»); the daily summary's materialized view hard-codes
 * `'total' AS traffic_category`, so «По категориям» could only ever return one bucket; and
 * «По адресам» needs `netflow_flows`, which no Go read function touches. Telephony
 * statistics have no data path whatever. `tabs` publishes what the server can actually
 * answer, and **the screen renders exactly what it names** — the nav rule of this whole app,
 * applied one level down. No operator toggle exists for the three, so a disabled tab would be
 * a control nobody can enable.
 *
 * ⚠ `earliest_date` IS THE RETENTION FLOOR AND THE PICKER MUST NOT OFFER EARLIER. The read
 * REFUSES a period starting before it (`409 pa.period_out_of_retention`) rather than clamping,
 * because a clamped range renders as «вы ничего не потратили в январе 2019» — a statement
 * about the subscriber instead of about the store.
 *
 * ============================================================================
 * ⚠ 3. `earliest_date` AND `data_since` ARE DIFFERENT FACTS, AND THE CUTOVER IS WHY
 * ============================================================================
 *
 * ~~The first day this store actually holds anything IS NOT ON THE WIRE, so the portal cannot
 * render it and must not guess it.~~ **STRUCK 2026-08-12 (the second review fix pass): it is
 * on the wire now, as `data_since`.** The sentence is kept struck because a vanished one
 * invites the same reasoning again — the screen used to have only a policy ceiling to reason
 * from, and everything it said about «no traffic» was built out of that.
 *
 *   `earliest_date`  a CEILING, computed: `today − retention`, floored at the tables' 1095-day
 *                    TTL. The same number for every account on the installation, and nothing
 *                    about it is measured. It answers «how far back MAY I ASK?».
 *   `data_since`     a MEASUREMENT: the first day this store actually holds a row FOR THIS
 *                    ACCOUNT, or `""` when it holds none / could not be asked. It answers
 *                    «how far back IS THERE ANYTHING?».
 *
 * After cutover the summaries begin on the day this system goes live, so EVERY pre-cutover
 * period answers `data_available: true` with an empty `days` array — the store WAS read, it
 * simply held nothing then. A screen with only `earliest_date` renders «трафика не
 * зафиксировано» there: a claim ABOUT THE SUBSCRIBER assembled entirely out of a fact about the
 * SERVER, told to a man who used 400 GB last year. `data_since` is what lets the screen say
 * «данные собираются с …» instead.
 *
 * ⚠ `""` IS A REAL ANSWER WITH TWO CAUSES A CLIENT MUST TREAT IDENTICALLY — a new Account with
 * no rows, and a store that could not be asked. Both mean «we cannot tell you when your history
 * begins», and neither justifies printing a date. It is NOT a date and must never be formatted
 * as one: `formatCivilDate('')` returns «—», so a screen that fed it through would print
 * «данные собираются с —».
 */

/** The adopted presets — `answers.md` **S32**. `month` is the default and the server's. */
export const STATS_PRESETS = ['day', 'week', 'month', 'quarter', 'half_year', 'year'] as const;
export type PAStatsPreset = (typeof STATS_PRESETS)[number];

/** The one tab this build can fill. See the file header for the three it cannot. */
export const STATS_TAB_DAYS = 'days';

/** `PAStatsDay` — one day of the «По дням» series. */
export interface PAStatsDay {
  /** A civil date. `summary_volume_daily.day` is a ClickHouse Date with no zone at all. */
  day: string;
  /** OCTETS, never megabytes — the portal owns the unit and the rounding. */
  input_octets: number;
  output_octets: number;
  total_octets: number;
  session_count: number;
  session_seconds: number;
}

/** `PAStats` — the whole §14 screen for one Account. */
export interface PAStats {
  account_id: number;
  login: string;
  /** The RESOLVED window, inclusive on both ends, as civil dates in the OPERATOR's zone. */
  from: string;
  to: string;
  /** The preset that produced the window, or "" when explicit dates were given. */
  preset: string;
  /** What this server can answer. ONE value today; the screen renders exactly these. */
  tabs: string[];
  /** ⚠ FALSE IS «WE CANNOT SEE», NEVER «NO TRAFFIC» — rule 1 in the file header. */
  data_available: boolean;
  /** The retention floor: the picker's `min`, and what the read refuses below. */
  earliest_date: string;
  /**
   * ⚠ THE FIRST DAY THIS STORE ACTUALLY HOLDS A ROW FOR THIS ACCOUNT — a MEASUREMENT, where
   * `earliest_date` beside it is a policy ceiling. `""` when the store holds none for him or
   * could not be asked: both mean «we cannot tell you when your history begins», and neither
   * is a date. See rule 3 in the file header for why the cutover makes this field load-bearing.
   */
  data_since: string;
  /** One row per day WITH DATA. A missing day is a GAP and not a zero — rule 2. */
  days: PAStatsDay[];
  /** Summed over the WHOLE window by the store, not over a page. Exact. */
  total_input_octets: number;
  total_output_octets: number;
  total_octets: number;
  total_session_count: number;
  total_session_seconds: number;
}

/** What the screen may ask for: a preset, OR an explicit inclusive range. */
export interface PAStatsPeriod {
  preset?: string;
  from?: string;
  to?: string;
}

// ---- calls ------------------------------------------------------------------

/**
 * getStatistics reads one Account's daily series for one period.
 *
 * ⚠ `from`/`to` WIN OVER `preset` ON THE SERVER AND BOTH ARE THEN REQUIRED — so this function
 * forwards WHICHEVER ENDS IT WAS GIVEN and neither completes the pair nor falls back to the
 * preset. The server's own switch is `from != "" || to != ""` → the explicit branch, and a
 * half-open pair is refused THERE: `400 pa.period_incomplete` (`details.field` = «from,to»),
 * or `400 pa.period_reversed` (`details.field` = «to») when `to` precedes `from`.
 *
 * ⚠ SENDING NEITHER END OF A HALF-OPEN PAIR IS THE SILENT FAILURE THIS SHAPE EXISTS TO
 * PREVENT, and it is what this function used to do. Sending the pair only when BOTH were
 * present meant a caller holding «с 1 марта» and no end sent NO PERIOD AT ALL — and a server
 * that is sent no period resolves its DEFAULT, `preset=month`. The subscriber was then shown
 * a different report, under a heading naming the window he did not ask for, with no error
 * anywhere: strictly worse than the refusal he had earned. The screen's local guards hid
 * that for as long as they stood, so the two must be removed together — which is why
 * `StatisticsScreen.test.tsx` asserts the half-open request CARRIES the end it was given
 * rather than only asserting that the refusal renders.
 *
 * ⚠ THE PRESET IS RESOLVED IN THE OPERATOR'S CALENDAR AND NOT HERE. «Этот месяц» is a
 * question about `general.billing_timezone`, which the browser does not know — this package
 * learned that the expensive way one domain along, where «this month» was built from a UTC
 * clock while the charges beside it carried a period from the billing zone's calendar.
 */
export function getStatistics(
  contractID: number,
  accountID: number,
  period: PAStatsPeriod = {},
): Promise<PAStats> {
  const q = new URLSearchParams({ contract_id: String(contractID) });
  // Mirrors the handler's own branch: ANY end given is an explicit-period request, and the
  // preset is what a request carrying no end at all falls back to. An `else` here would let
  // a half-open pair arrive as a preset — see the header.
  if (period.from || period.to) {
    if (period.from) q.set('from', period.from);
    if (period.to) q.set('to', period.to);
  } else if (period.preset) {
    q.set('preset', period.preset);
  }
  return api.get<PAStats>(`/pa/statistics/${accountID}?${q.toString()}`);
}
